import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge, TreatmentBadge } from "@/components/Badges";
import { SmartImage } from "@/components/SmartImage";
import { api, fmtDate, fmtDay, jobTitle, ms, usd } from "@/lib/api";
import { useMode } from "@/lib/mode";
import { useSeo } from "@/lib/seo";
import { useFreshMedia } from "@/hooks/useFreshMedia";
import { RefreshCw, Search, Sparkles, ArrowRight, Images, Home, Building2 } from "lucide-react";

export default function JobsPage() {
  const nav = useNavigate();
  const { paths, mode } = useMode();
  useSeo({ title: mode === "lab" ? "Lab · Jobber" : "Mine annonser", description: "Alle annonsene dine – status, bilder og resultater.", noindex: true });
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const load = async () => {
    const [j, s] = await Promise.all([
      api.get("/v1/jobs", { params: { limit: 100, ...(status !== "all" ? { status } : {}) } }),
      mode === "lab" ? api.get("/lab/stats").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
    ]);
    setItems(j.data.items || []);
    setStats(s.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);
  useEffect(() => {
    if (!items?.some((j) => ["queued", "processing", "reviewing"].includes(j.status))) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);
  useFreshMedia(load, { enabled: Boolean(items) });
  const filtered = (items || []).filter((j) => !q || `${j.external_property_id} ${j.job_id} ${j.title || ""} ${j.source_title || ""}`.toLowerCase().includes(q.toLowerCase()));
  const customer = mode === "customer";
  const nImages = (j) => j.images_count ?? j.image_count ?? j.images_total ?? "–";

  return (
    <div className="mx-auto w-full max-w-[1400px] px-5 py-10 sm:px-8 sm:py-14 space-y-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="eyebrow">{customer ? "Mine annonser" : "Jobber"}</div>
          <h1 className="display mt-3 text-[44px] sm:text-[60px]">{customer ? "Annonsene dine" : "Alle jobber"}<span className="dot">.</span></h1>
          <p className="mt-3 text-[16px] text-dim">{customer ? "Hver annonse har sitt eget galleri, forslag og annonseutkast." : "Alle kjøringer på tvers av kunder og kilder."}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground/40" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk…" className="pl-10 w-56" data-testid="jobs-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-11 w-44 rounded-[12px] bg-card" data-testid="jobs-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle statuser</SelectItem>
              <SelectItem value="completed">Ferdig</SelectItem>
              <SelectItem value="processing">Kjører</SelectItem>
              <SelectItem value="queued">I kø</SelectItem>
              <SelectItem value="failed">Feilet</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" className="h-11 w-11 rounded-[12px]" onClick={load} aria-label="Oppdater" data-testid="jobs-refresh"><RefreshCw className="h-4 w-4" /></Button>
          <Button onClick={() => nav(paths.home)} data-testid="jobs-new"><Sparkles className="h-4 w-4" />Ny annonse</Button>
        </div>
      </div>

      {!customer && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="jobs-stats">
          {(() => {
            const bv = stats.by_version || [];
            const sum = (k) => bv.reduce((a, v) => a + (v[k] || 0), 0);
            return [["Jobber", stats.jobs_total ?? sum("jobs")], ["Ferdige", stats.jobs_completed ?? sum("completed")], ["Bilder behandlet", stats.images_total ?? sum("images")], ["Estimert kostnad", usd(stats.cost_total_usd ?? sum("cost_usd"))]];
          })().map(([l, v]) => (
            <div key={l} className="panel p-5"><div className="eyebrow">{l}</div><div className="font-heading text-[32px] metric-num mt-1.5 tracking-[-0.02em]">{v ?? "–"}</div></div>
          ))}
        </div>
      )}

      {items === null ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-72 rounded-[20px]" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="panel p-12 sm:p-16 text-center space-y-4" data-testid="jobs-empty">
          <div className="h-12 w-12 rounded-[14px] bg-[hsl(var(--accent))] text-[hsl(var(--ink))] flex items-center justify-center mx-auto"><Images className="h-5 w-5" /></div>
          <h2 className="display-sm text-[32px]">Ingen annonser enda<span className="dot">.</span></h2>
          <p className="text-dim text-[15px]">Lim inn en FINN- eller Airbnb-lenke, så gjør DigiHome resten.</p>
          <Button onClick={() => nav(paths.home)} data-testid="jobs-empty-cta"><Sparkles className="h-4 w-4" />Lag din første annonse</Button>
        </div>
      ) : customer ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="jobs-grid">
          {filtered.map((j) => (
            <button key={j.job_id} type="button" onClick={() => nav(paths.job(j.job_id))} className="group rounded-[20px] bg-card ring-hair overflow-hidden text-left hover:shadow-[0_0_0_1px_hsl(var(--foreground)/0.3),var(--shadow-card)] transition-[box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`job-card-${j.job_id}`}>
              <div className="relative image-canvas aspect-[16/10]">
                {j.hero_thumbnail_url ? <SmartImage src={j.hero_thumbnail_url} alt="" /> : <div className="h-full w-full flex items-center justify-center text-muted-foreground"><Images className="h-6 w-6" /></div>}
                <div className="absolute top-3 left-3"><StatusBadge status={j.status} /></div>
                {j.rental_type && <div className="absolute top-3 right-3 rounded-[8px] bg-white/90 text-[hsl(var(--ink))] px-2 py-1 text-[11.5px] font-medium inline-flex items-center gap-1 backdrop-blur">{j.rental_type === "short_term" ? <Building2 className="h-3 w-3" /> : <Home className="h-3 w-3" />}{j.rental_type === "short_term" ? "Korttid" : "Langtid"}</div>}
              </div>
              <div className="p-5 space-y-2">
                <div className="font-heading text-[20px] tracking-[-0.015em] leading-snug line-clamp-2" data-testid={`job-card-title-${j.job_id}`}>{jobTitle(j)}</div>
                <div className="text-[13px] text-dim flex flex-wrap items-center gap-x-2">
                  <span>{nImages(j)} bilder</span><span>·</span><span>{fmtDay(j.created_at)}</span>
                  {j.status === "processing" && <span className="text-[hsl(var(--accent-strong))] font-medium">{j.progress?.percent ?? 0}%</span>}
                </div>
                {j.status === "completed" && j.treatment_counts && (
                  <div className="flex flex-wrap items-center gap-1.5 pt-0.5" data-testid={`job-card-treatments-${j.job_id}`}>
                    {["optimize_only", "soft_style", "virtual_stage"].map((k) => j.treatment_counts[k] ? <span key={k} className="inline-flex items-center gap-1"><TreatmentBadge kind={k} size="xs" /><span className="text-[12px] text-dim">{j.treatment_counts[k]}</span></span> : null)}
                  </div>
                )}
                <div className="pt-1 text-[14px] font-medium inline-flex items-center gap-1 text-foreground">Åpne<ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="panel overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bolig</TableHead><TableHead>Status</TableHead><TableHead>Bilder</TableHead><TableHead>Type</TableHead><TableHead>Kjøretid</TableHead><TableHead>Kostnad</TableHead><TableHead>Opprettet</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((j) => (
                <TableRow key={j.job_id} className="cursor-pointer" onClick={() => nav(paths.job(j.job_id))} data-testid={`job-row-${j.job_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-14 rounded-[8px] overflow-hidden image-canvas shrink-0">{j.hero_thumbnail_url && <SmartImage src={j.hero_thumbnail_url} alt="" />}</div>
                      <div className="min-w-0"><div className="font-medium truncate max-w-[280px]">{jobTitle(j, { technical: true })}</div><div className="font-mono text-[11px] text-muted-foreground">{j.job_id}</div></div>
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge status={j.status} /></TableCell>
                  <TableCell className="metric-num">{nImages(j)}</TableCell>
                  <TableCell className="text-xs">{j.rental_type === "short_term" ? "Korttid" : "Langtid"}</TableCell>
                  <TableCell className="font-mono text-xs">{ms(j.metrics?.total_ms ?? j.total_ms)}</TableCell>
                  <TableCell className="font-mono text-xs">{usd(j.metrics?.cost_estimate_usd ?? j.cost_estimate_usd)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(j.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
